import clsx from "clsx";
import { FC, Fragment } from "react";
import "swiper/scss";
import "swiper/scss/pagination";
import { Swiper } from "swiper/types";
import IconArrow from "../../public/image/svg/arrow.svg";
import styles from "./Swiper.module.scss";

interface SwiperNavigationProps {
  swiper?: Swiper;
  perPage: number;
  direction: "next" | "prev";
  variation: "fraction" | "number" | "arrow" | "square";
  hideOnEdge?: boolean;
  onDark?: boolean;
  currentPage?: number;
  className?: string;
  totalFilteredPages?: number;
}

const SwiperNavigation: FC<SwiperNavigationProps> = ({
  swiper,
  direction = "next",
  variation = "arrow",
  hideOnEdge = false,
  onDark = false,
  className,
  currentPage,
  totalFilteredPages,
}) => {
  if (!swiper) return null;

  const totalPages =
    Number.isInteger(totalFilteredPages) && totalFilteredPages > 0
      ? totalFilteredPages
      : swiper?.pagination?.bullets?.length;

  const pageToNavigateTo = currentPage + 1 * (direction === "next" ? 1 : -1);
  const ariaLabel = (): string => {
    const baseLabel =
      direction === "next"
        ? "Go to the Next Slide"
        : "Go to the Previous Slide";

    if (variation === "fraction") {
      return `Slide ${currentPage} of ${totalPages}. ${baseLabel}`;
    } else if (variation === "number") {
      return `Slide ${currentPage}. ${baseLabel}`;
    }

    return baseLabel;
  };

  const pad = (num: number): string => {
    return num?.toString()?.padStart(2, "0");
  };

  if (
    hideOnEdge &&
    ((direction === "prev" && pageToNavigateTo < 1) ||
      (direction === "next" && pageToNavigateTo > totalPages))
  )
    return null;

  return (
    <button
      className={clsx(
        styles["swiper-navigation"],
        styles[`swiper-navigation--${direction}`],
        styles[`swiper-navigation--${variation}`],
        className,
        {
          [styles["swiper-navigation--light"]]: onDark,
        }
      )}
      onClick={() => {
        if (direction === "prev") {
          swiper.slidePrev();
        } else {
          swiper.slideNext();
        }
      }}
      aria-label={ariaLabel()}
    >
      {["fraction", "number"].includes(variation) && (
        <span
          className={styles["swiper-navigation__numbers"]}
          aria-hidden="true"
        >
          {pad(currentPage)}
          {variation === "fraction" && (
            <Fragment>
              <strong>/</strong>
              {pad(totalPages)}
            </Fragment>
          )}
        </span>
      )}
      <IconArrow className={styles["swiper-navigation__arrow"]} />
    </button>
  );
};

export default SwiperNavigation;
